import {
  AloftArea,
  Improvements,
  LocationType,
  ORDER_FEATURES,
  SiteRating,
  SixTierQuality,
  isDefined,
  orUndefined,
  schAloftArea,
} from '@aloft/toolkit-shared';

// Materiality thresholds for comp adjustment alerts. A material difference means subject and comp
// differ enough that an adjustment is typically expected; immaterial gaps are ignored.

// GLA: >5% difference vs subject sqft. Test: "treats GLA differences above/at or below 5%".
export const GLA_MATERIALITY_THRESHOLD = 0.05;
// Urban/Suburban site: >25% difference vs subject sqft. Test: "treats urban site size differences above/at or below 25%".
export const URBAN_SUBURBAN_SITE_SIZE_MATERIALITY_THRESHOLD = 0.25;
// Basement/ADU area: >15% difference. Kept separate from GLA so those features retain the wider band.
export const BASEMENT_ADU_AREA_MATERIALITY_THRESHOLD = 0.15;
// Subject site-size source conflict: >10% difference across data sources (QC inconsistentSubjectSiteSize).
export const SUBJECT_SITE_SIZE_SOURCE_CONFLICT_THRESHOLD = 0.1;
// Rural site: >1 acre absolute difference. Test: "treats rural site size differences above 1 acre".
export const RURAL_SITE_SIZE_MATERIALITY_ACRES = 1;
// Baths: >0 full bath difference (i.e. ±1), or >1 half baths (i.e. ±2) between subject and comp.
export const FULL_BATH_MATERIALITY_THRESHOLD = 0;
export const HALF_BATH_MATERIALITY_THRESHOLD = 1;
// Garage/carport: >0 parking space difference (i.e. ±1) when both properties have the same parking type.
export const GARAGE_CARPORT_SPACE_MATERIALITY_THRESHOLD = 0;

type ParkingSubtype = 'attachedGarage' | 'detachedGarage' | 'carport';

const getParkingSubtype = (improvements?: Improvements): ParkingSubtype | undefined => {
  if (ORDER_FEATURES.garage.hasFeature({ garage: improvements?.garage } as Improvements))
    return 'attachedGarage';
  if (
    ORDER_FEATURES.detachedGarage.hasFeature({
      detachedGarage: improvements?.detachedGarage,
    } as Improvements)
  )
    return 'detachedGarage';
  if (ORDER_FEATURES.carport.hasFeature({ carport: improvements?.carport } as Improvements))
    return 'carport';
  return undefined;
};

const getParkingSpaces = (
  improvements: Improvements | undefined,
  parkingSubtype: ParkingSubtype | undefined,
): number | undefined => {
  if (parkingSubtype === 'attachedGarage') return improvements?.garage?.spaces;
  if (parkingSubtype === 'detachedGarage') return improvements?.detachedGarage?.spaces;
  if (parkingSubtype === 'carport') return improvements?.carport?.spaces;
  return undefined;
};

export const getGarageCarportMaterialityParams = (improvements?: Improvements) => {
  const parkingSubtype = getParkingSubtype(improvements);
  return {
    hasFeature:
      ORDER_FEATURES.garage.hasFeature({ garage: improvements?.garage } as Improvements) ||
      ORDER_FEATURES.detachedGarage.hasFeature({
        detachedGarage: improvements?.detachedGarage,
      } as Improvements) ||
      ORDER_FEATURES.carport.hasFeature({ carport: improvements?.carport } as Improvements),
    spaces: getParkingSpaces(improvements, parkingSubtype),
    parkingSubtype,
  };
};

export const getBasementMaterialityParams = (improvements?: Improvements) => ({
  hasFeature: ORDER_FEATURES.basement.hasFeature({
    basement: improvements?.basement,
  } as Improvements),
  totalArea: orUndefined(schAloftArea.parse)(improvements?.basement?.totalArea),
  finishedArea: orUndefined(schAloftArea.parse)(improvements?.basement?.finishedArea),
});

// True when one area is present/non-zero and the other absent/zero (a categorical finish-level change),
// or when both are non-zero and differ by more than the threshold. Both zero/absent is immaterial.
const areaDifferenceIsMaterial = (
  subject: AloftArea | undefined,
  comp: AloftArea | undefined,
  threshold: number,
): boolean => {
  const subjectSqft = subject?.toSqFt().unitAmount ?? 0;
  const compSqft = comp?.toSqFt().unitAmount ?? 0;
  if (subjectSqft === 0 && compSqft === 0) return false;
  if (subjectSqft === 0 || compSqft === 0) return true;
  return Math.abs(compSqft - subjectSqft) / subjectSqft > threshold;
};

// Returns true when |comp - subject| / subject sqft exceeds threshold. False when subject sqft is zero.
const exceedsSubjectPercentageThreshold = (
  subject: AloftArea,
  comp: AloftArea,
  threshold: number,
): boolean => {
  const subjectSqft = subject.toSqFt().unitAmount;
  if (subjectSqft === 0) return false;

  const compSqft = comp.toSqFt().unitAmount;
  return Math.abs(compSqft - subjectSqft) / subjectSqft > threshold;
};

export const areasExceedPercentageThreshold = (areas: AloftArea[], threshold: number): boolean => {
  const sqftValues = areas
    .filter((area) => !area.equals(AloftArea.zero))
    .map((area) => area.toSqFt().unitAmount);
  if (sqftValues.length < 2) return false;
  const min = Math.min(...sqftValues);
  if (min === 0) return false;

  const max = Math.max(...sqftValues);
  return (max - min) / min > threshold;
};

export const isSubjectSiteSizeSourceConflict = (areas: AloftArea[]): boolean =>
  areasExceedPercentageThreshold(areas, SUBJECT_SITE_SIZE_SOURCE_CONFLICT_THRESHOLD);

// Gross living area. Material when comp GLA differs from subject by more than 5%.
// Zero or missing GLA is immaterial. Test: "treats GLA differences...", "treats zero GLA values...".
export const isGlaAdjustmentMaterial = (
  subjectGla: AloftArea | undefined,
  compGla: AloftArea | undefined,
): boolean => {
  if (!isDefined(subjectGla) || !isDefined(compGla)) return false;
  if (subjectGla.equals(AloftArea.zero) || compGla.equals(AloftArea.zero)) return false;

  return exceedsSubjectPercentageThreshold(subjectGla, compGla, GLA_MATERIALITY_THRESHOLD);
};

// Lot/site area. Urban and Suburban: material when sqft differs by more than 25% vs subject.
// Rural: material when absolute acre difference exceeds 1 acre (percentage rule does not apply).
// Zero or missing site area is immaterial. Test: "treats urban site size...", "treats rural site size...".
export const isSiteSizeAdjustmentMaterial = (
  subjectSite: AloftArea | undefined,
  compSite: AloftArea | undefined,
  locationType?: LocationType,
): boolean => {
  if (!isDefined(subjectSite) || !isDefined(compSite)) return false;
  if (subjectSite.equals(AloftArea.zero) || compSite.equals(AloftArea.zero)) return false;

  if (locationType === 'Rural') {
    const diffAcres = Math.abs(compSite.toAcre().unitAmount - subjectSite.toAcre().unitAmount);
    return diffAcres > RURAL_SITE_SIZE_MATERIALITY_ACRES;
  }

  return exceedsSubjectPercentageThreshold(
    subjectSite,
    compSite,
    URBAN_SUBURBAN_SITE_SIZE_MATERIALITY_THRESHOLD,
  );
};

// Site influence (e.g. Neutral vs Beneficial). Material when both ratings are present and differ.
// Test: "treats rating-level features as material only when ratings differ" (site influence case).
export const isSiteInfluenceAdjustmentMaterial = (
  subjectInfluence: SiteRating | undefined,
  compInfluence: SiteRating | undefined,
): boolean =>
  isDefined(subjectInfluence) && isDefined(compInfluence) && subjectInfluence !== compInfluence;

// View rating. Same rule as site influence: material only when both ratings exist and differ.
// Test: "treats rating-level features..." (view case with matching Neutral ratings).
export const isViewAdjustmentMaterial = (
  subjectView: SiteRating | undefined,
  compView: SiteRating | undefined,
): boolean => isDefined(subjectView) && isDefined(compView) && subjectView !== compView;

// UAD six-tier quality. Material when both tiers are present and differ (any tier step counts).
export const isQualityAdjustmentMaterial = (
  subjectQuality: SixTierQuality | undefined,
  compQuality: SixTierQuality | undefined,
): boolean => isDefined(subjectQuality) && isDefined(compQuality) && subjectQuality !== compQuality;

// UAD six-tier condition. Material when both tiers are present and differ.
// Test: "treats rating-level features..." (condition 4 vs 5 / 4 vs 4 cases).
export const isConditionAdjustmentMaterial = (
  subjectCondition: SixTierQuality | undefined,
  compCondition: SixTierQuality | undefined,
): boolean =>
  isDefined(subjectCondition) && isDefined(compCondition) && subjectCondition !== compCondition;

// Bath count. Material when full baths differ by more than 1, or half baths by more than 2.
// Full-bath check runs first; half-bath check requires both half-bath counts. Thresholds in test: "exports the documented thresholds".
export const isBathAdjustmentMaterial = (
  subjectFullBaths: number | undefined,
  subjectHalfBaths: number | undefined,
  compFullBaths: number | undefined,
  compHalfBaths: number | undefined,
): boolean => {
  if (!isDefined(subjectFullBaths) || !isDefined(compFullBaths)) return false;
  if (Math.abs(compFullBaths - subjectFullBaths) > FULL_BATH_MATERIALITY_THRESHOLD) return true;

  if (!isDefined(subjectHalfBaths) || !isDefined(compHalfBaths)) return false;
  return Math.abs(compHalfBaths - subjectHalfBaths) > HALF_BATH_MATERIALITY_THRESHOLD;
};

// Garage or carport. Material when presence differs, parking subtypes differ, or when both have the
// same subtype and space count differs by more than 1.
export const isGarageCarportAdjustmentMaterial = ({
  subjectHasFeature,
  compHasFeature,
  subjectSpaces,
  compSpaces,
  subjectParkingType,
  compParkingType,
}: {
  subjectHasFeature?: boolean;
  compHasFeature?: boolean;
  subjectSpaces?: number;
  compSpaces?: number;
  subjectParkingType?: string;
  compParkingType?: string;
}): boolean => {
  if (!isDefined(subjectHasFeature) || !isDefined(compHasFeature)) return false;
  if (subjectHasFeature !== compHasFeature) return true;
  if (!subjectHasFeature || !compHasFeature) return false;

  if (
    subjectParkingType !== compParkingType &&
    isDefined(subjectParkingType) &&
    isDefined(compParkingType)
  ) {
    return true;
  }

  if (
    subjectParkingType === compParkingType &&
    isDefined(subjectParkingType) &&
    isDefined(compParkingType) &&
    isDefined(subjectSpaces) &&
    isDefined(compSpaces)
  ) {
    return Math.abs(compSpaces - subjectSpaces) > GARAGE_CARPORT_SPACE_MATERIALITY_THRESHOLD;
  }

  return false;
};

// Basement. Material when presence differs, when the finish level differs (finished area present on
// only one side), or when total or finished area differ by more than 15%. Same finish level with both
// areas within 15% is immaterial.
export const isBasementAdjustmentMaterial = ({
  subjectHasFeature,
  compHasFeature,
  subjectTotalArea,
  compTotalArea,
  subjectFinishedArea,
  compFinishedArea,
}: {
  subjectHasFeature?: boolean;
  compHasFeature?: boolean;
  subjectTotalArea?: AloftArea;
  compTotalArea?: AloftArea;
  subjectFinishedArea?: AloftArea;
  compFinishedArea?: AloftArea;
}): boolean => {
  if (!isDefined(subjectHasFeature) || !isDefined(compHasFeature)) return false;
  if (subjectHasFeature !== compHasFeature) return true;
  if (!subjectHasFeature) return false;

  return (
    areaDifferenceIsMaterial(
      subjectTotalArea,
      compTotalArea,
      BASEMENT_ADU_AREA_MATERIALITY_THRESHOLD,
    ) ||
    areaDifferenceIsMaterial(
      subjectFinishedArea,
      compFinishedArea,
      BASEMENT_ADU_AREA_MATERIALITY_THRESHOLD,
    )
  );
};

// Basement bath count. Same thresholds as above-grade baths via isBathAdjustmentMaterial.
export const isBasementBathAdjustmentMaterial = (
  subjectFullBaths: number | undefined,
  subjectHalfBaths: number | undefined,
  compFullBaths: number | undefined,
  compHalfBaths: number | undefined,
): boolean =>
  isBathAdjustmentMaterial(subjectFullBaths, subjectHalfBaths, compFullBaths, compHalfBaths);

// Binary present/absent features (e.g. pool, fireplace). Material when both values are known and differ.
export const isPresentOrNotAdjustmentMaterial = (
  subjectHasFeature: boolean | undefined,
  compHasFeature: boolean | undefined,
): boolean =>
  isDefined(subjectHasFeature) && isDefined(compHasFeature) && subjectHasFeature !== compHasFeature;
